import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useUser } from '../../contexts/UserContext';
import { getPublicNoticesUrl } from '../../utils/UrlUtilities';
import { NOTICE_TYPE_COLORS } from '../../utils/Constants';
import Carousel from './Carousel';

const CARD_W = 300;
const CARD_H = 156;
// Heuristic: a notice whose body clearly won't fit the fixed box gets a "Read more" link.
const isLong = (n) => {
    const body = n.body || '';
    return body.length > 150 || body.split('\n').length > 4 || (n.title || '').length > 80;
};

const colorFor = (n) => NOTICE_TYPE_COLORS[n.notice_type] || NOTICE_TYPE_COLORS.generic;

// Reorder notices so all notices of the same level sit together (side by side), while keeping
// the overall order (recency) of each level's first appearance.
function groupByLevel(list) {
    const order = [];
    const groups = new Map();
    for (const n of list) {
        const lv = n.level || '';
        if (!groups.has(lv)) { groups.set(lv, []); order.push(lv); }
        groups.get(lv).push(n);
    }
    return order.flatMap(lv => groups.get(lv));
}

// The in-app destination for a clickable notice (null = not clickable):
//   result publish  -> that competition's scoreboard (leaderboard)
//   competition ongoing -> the Competition tab of the Quiz Store at that level
function noticeTarget(n) {
    if (!n.quiz_id) return null;
    const level = encodeURIComponent(n.level || '');
    if (n.notice_type === 'quiz_result') {
        return `/userquiz?view=COMPETITION_QUIZ&level=${level}&quiz=${n.quiz_id}&show=leaderboard`;
    }
    if (n.notice_type === 'new_quiz_ongoing') {
        return `/userquiz?view=COMPETITION_QUIZ&level=${level}`;
    }
    return null;
}

// Public Notice Board — visible without login. Pinned notices are summarized as compact lines
// right under the heading (type · level · title, one per line); ALL notices (pinned included)
// run in the carousel, grouped by level and colored by notice type. Long notices expose a
// "Read more" link that opens the full text below.
export default function NoticeBoardCarousel() {
    const CallApi = useApiCaller();
    const navigate = useNavigate();
    const { userState } = useUser();
    const [notices, setNotices] = useState([]);
    const [expanded, setExpanded] = useState(null); // the notice shown in full at the bottom

    // Navigate to a notice's destination; unauthenticated users go via login (then back).
    const goToNotice = useCallback((n) => {
        const target = noticeTarget(n);
        if (!target) return;
        if (userState?.isLoggedIn) {
            navigate(target);
        } else {
            navigate(`/login?redirect=${encodeURIComponent(target)}`);
        }
    }, [navigate, userState?.isLoggedIn]);

    const fetchNotices = useCallback(async () => {
        const res = await CallApi({ url: getPublicNoticesUrl(50), method: 'GET', token: false });
        if (res.success) setNotices(res.data || []);
    }, [CallApi]);

    useEffect(() => {
        fetchNotices();
        // Poll + refetch on focus so admin create/edit/delete reflects on the board without reload.
        const timer = setInterval(fetchNotices, 30000);
        const onFocus = () => { if (document.visibilityState === 'visible') fetchNotices(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            clearInterval(timer);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [fetchNotices]);

    // If the expanded notice was deleted/unpublished in a refresh, drop the panel.
    useEffect(() => {
        if (expanded && !notices.some(n => n.notice_id === expanded.notice_id)) setExpanded(null);
    }, [notices, expanded]);

    // The board renders exactly what the API returns — /public-notices already filters to
    // published notices inside their [starts_at, ends_at] window (judged in UTC, so correct in
    // any zone) and orders pinned-first. Pinned ones are also surfaced as fixed summary lines
    // above the carousel; every returned notice runs in the rotating carousel.
    const pinned = groupByLevel(notices.filter(n => n.is_pinned));
    const all = groupByLevel(notices);

    const card = (n) => {
        const c = colorFor(n);
        const long = isLong(n);
        const clickable = !!noticeTarget(n);
        return (
            <div
                key={n.notice_id}
                className="kp-card-hover"
                onClick={clickable ? () => goToNotice(n) : undefined}
                title={clickable ? 'Open' : undefined}
                style={{
                    position: 'relative',
                    width: CARD_W, height: CARD_H,
                    backgroundColor: c.bg,
                    border: `1px solid ${c.border}`,
                    borderLeft: `6px solid ${c.border}`,
                    borderRadius: '0.75rem',
                    padding: '0.9rem 1.1rem',
                    color: c.text,
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    cursor: clickable ? 'pointer' : 'default',
                }}
            >
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.8 }}>
                    {n.is_pinned ? '📌 ' : ''}{n.notice_type.replace(/_/g, ' ')}{n.level ? ` · ${n.level}` : ''}
                </div>
                <div style={{ fontWeight: 700, fontSize: '1rem', margin: '0.3rem 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                <div style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', overflow: 'hidden', flex: 1 }}>{n.body}</div>
                {long && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setExpanded(n); }}
                        style={{
                            position: 'absolute', bottom: 6, right: 10,
                            border: 'none', background: 'transparent', color: c.text,
                            fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline',
                        }}
                    >
                        Read more →
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="my-5">
            <h2 className="text-center mb-2 fw-bold" style={{ color: '#4c1d95' }}>📢 Notice Board</h2>

            {/* Pinned notices: compact one-line summaries (type · level · title) under the heading. */}
            {pinned.length > 0 && (
                <div className="text-center mb-3">
                    {pinned.map(n => {
                        const c = colorFor(n);
                        const clickable = !!noticeTarget(n);
                        return (
                            <div
                                key={n.notice_id}
                                onClick={clickable ? () => goToNotice(n) : undefined}
                                title={clickable ? 'Open' : undefined}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                                    maxWidth: '100%', margin: '0.15rem auto', padding: '0.3rem 0.8rem',
                                    backgroundColor: c.bg, color: c.text,
                                    border: `1px solid ${c.border}`, borderLeft: `5px solid ${c.border}`,
                                    borderRadius: '0.5rem', fontWeight: 600, fontSize: '0.9rem',
                                    cursor: clickable ? 'pointer' : 'default',
                                }}
                            >
                                <span>📌</span>
                                <span style={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.03em', opacity: 0.85 }}>
                                    {n.notice_type.replace(/_/g, ' ')}{n.level ? ` · ${n.level}` : ''}
                                </span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {n.title}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {notices.length === 0 ? (
                <div className="text-center text-muted py-3">No notices right now.</div>
            ) : (
                <Carousel>
                    {all.map(card)}
                </Carousel>
            )}

            {expanded && (() => {
                const c = colorFor(expanded);
                return (
                    <div
                        className="mt-3 mx-auto"
                        style={{
                            maxWidth: 900,
                            backgroundColor: c.bg,
                            border: `1px solid ${c.border}`,
                            borderLeft: `6px solid ${c.border}`,
                            borderRadius: '0.75rem',
                            padding: '1.25rem 1.5rem',
                            color: c.text,
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-start">
                            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.8 }}>
                                {expanded.notice_type.replace(/_/g, ' ')}{expanded.level ? ` · ${expanded.level}` : ''}
                            </div>
                            <button onClick={() => setExpanded(null)} className="btn btn-sm btn-link p-0" style={{ color: c.text, fontWeight: 700 }}>Close ✕</button>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '1.2rem', margin: '0.4rem 0' }}>{expanded.title}</div>
                        <div style={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>{expanded.body}</div>
                        {noticeTarget(expanded) && (
                            <button
                                className="btn btn-sm mt-3"
                                style={{ background: c.border, color: '#fff', fontWeight: 600 }}
                                onClick={() => goToNotice(expanded)}
                            >
                                {expanded.notice_type === 'quiz_result' ? 'View scoreboard →' : 'Go to competition →'}
                            </button>
                        )}
                    </div>
                );
            })()}
        </div>
    );
}
